import { footprintBbox, type Bbox, type MapFrame2d } from "./planRaster";

/**
 * A 2D floor plan derived from the warehouse's own zone polygons (ADR 303) — the
 * last-resort source behind an uploaded plan and the archived SLAM raster.
 *
 * <p>A warehouse built through the zones→GLB path (ADR 155/158) never produces a SLAM
 * raster, because nothing ever drove it. Until now that meant no 2D plan at all: the
 * Planta 2D layer simply did not exist for four of the six sites. But the twin already
 * holds the footprints it would draw — deriving the plan from those costs no storage, no
 * upload and no walk, and it cannot go stale against the structure the way a generated
 * raster would.
 *
 * <p>It is a schematic, not a survey: it shows where the aisles are, not where the walls
 * are. That is the honest limit of the input and the reason it sits LAST in the
 * precedence — an operator's own drawing and a machine's scan of the real building both
 * describe more than the twin's own footprints do.
 *
 * <p>The frame maths lives here, apart from the drawing, because jsdom has no 2-D canvas
 * context: everything below is unit-testable, and only {@link drawZonesPlan} needs pixels.
 */

/** Fraction of the content extent left as breathing room on every side. */
const MARGIN = 0.04;

/** Below this the plan is a smudge; a one-aisle warehouse still gets a usable frame. */
const MIN_EDGE_PX = 160;

/**
 * The raster frame that covers `polygons` at up to `maxEdgePx` on the long axis.
 *
 * <p>Resolution is metres per pixel and is chosen from the long axis, so a long thin
 * warehouse (JSL: 13 aisles in a row) stays legible instead of being squeezed to fit a
 * square. `originX/originY` name the frame's TOP-LEFT corner — the (min x, min y)
 * corner, since the display frame is image-like (ADR 311) — so the markers and the
 * view footprint that already read this frame land without knowing where it came from.
 */
export function zonesPlanFrame(
  polygons: ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>,
  maxEdgePx: number,
): MapFrame2d | null {
  const b: Bbox | null = footprintBbox(polygons);
  if (!b) return null;
  const contentW = Math.max(b.maxX - b.minX, 0.001);
  const contentH = Math.max(b.maxY - b.minY, 0.001);
  const padX = contentW * MARGIN;
  const padY = contentH * MARGIN;
  const widthM = contentW + padX * 2;
  const heightM = contentH + padY * 2;

  // One resolution for both axes — a per-axis fit would stretch the building.
  const longM = Math.max(widthM, heightM);
  const resolution = longM / Math.max(maxEdgePx, MIN_EDGE_PX);
  if (!Number.isFinite(resolution) || resolution <= 0) return null;

  return {
    widthPx: Math.max(1, Math.round(widthM / resolution)),
    heightPx: Math.max(1, Math.round(heightM / resolution)),
    resolution,
    originX: b.minX - padX,
    originY: b.minY - padY,
  };
}

/** A polygon in frame pixels, ready to stroke/fill. Same image-like mapping as
 *  {@link worldToPixel} (ADR 311): y runs straight down the rows. */
export function polygonToPixels(
  frame: MapFrame2d, polygon: ReadonlyArray<ReadonlyArray<number>>,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const p of polygon) {
    if (p.length < 2 || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) continue;
    out.push([
      (p[0] - frame.originX) / frame.resolution,
      (p[1] - frame.originY) / frame.resolution,
    ]);
  }
  return out;
}

/** Usable polygons only: a zone with no polygon, or a degenerate one, draws nothing. */
export function drawablePolygons(
  zones: ReadonlyArray<{ polygon: number[][] | null }>,
): number[][][] {
  return zones
    .map((z) => z.polygon)
    .filter((p): p is number[][] => Array.isArray(p) && p.length >= 3);
}

/**
 * Rasterise the footprints into the frame, in the same two tokens the SLAM raster is
 * themed with (blue-300 outline, blue-500 wash) so the two sources read as one surface.
 *
 * <p>Returns null where there is no 2-D context — jsdom, and any browser that refuses
 * one. The caller then falls through to no minimap, exactly as it does for an
 * undecodable plan.
 */
export function drawZonesPlan(
  frame: MapFrame2d, polygons: ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>,
): ImageData | null {
  const canvas = document.createElement("canvas");
  canvas.width = frame.widthPx;
  canvas.height = frame.heightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "rgba(30, 91, 216, 0.18)";   // blue-500 wash — the free/used floor
  ctx.strokeStyle = "rgba(166, 194, 247, 0.9)"; // blue-300 — the footprint edge
  // Hairline at any frame size: the plan is a schematic, and a 3 px border on a 160 px
  // frame would swallow the aisle it outlines.
  ctx.lineWidth = Math.max(1, Math.round(Math.min(frame.widthPx, frame.heightPx) / 400));
  ctx.lineJoin = "round";

  for (const poly of polygons) {
    const pts = polygonToPixels(frame, poly);
    if (pts.length < 3) continue;
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  return ctx.getImageData(0, 0, frame.widthPx, frame.heightPx);
}
